
import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Box,
    Typography,
    Collapse,
    Button,
    Grid,
    Divider,
    Chip
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/ArticleOutlined';
import DescriptionIcon from '@mui/icons-material/DocumentScannerOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import maquiHead from '../../../../img/maqui_head.png';
import { usePallet } from './../../../../contexts/PalletContext';
import ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService, {
    Mensagem,
    DataAcaoPreparatoria,
    INotaSimilar,
    INotaContexto
} from '../../../../services/ProposicaoAcaoPreparatoriaNotaTecnicaColaborativaService';
import RespostaIA from '../../../../components/ia/RespostaIA';

interface ConversaModalProps {
    idAcaoPreparatoria: number;
    open: boolean;
    onClose: () => void;
    lawText?: string;
    summaryText?: string;
}

const ConversaModal: React.FC<ConversaModalProps> = ({
    idAcaoPreparatoria,
    open,
    onClose,
    lawText = '',
    summaryText = ''
}) => {
    const { pallet } = usePallet();

    // 1) Guarda todos os dados da nota técnica, incluindo o objeto { conversa_ia: Mensagem[] }
    const [dataAcaoPreparatoria, setDataAcaoPreparatoria] = useState<DataAcaoPreparatoria>(
        {} as DataAcaoPreparatoria
    );

    // 2) Estado local para renderizar as mensagens (Somente Mensagem[])
    const [conversas, setConversas] = useState<Mensagem[]>([]);

    const [inputValue, setInputValue] = useState('');
    const [blockSendButton, setBlockSendButton] = useState(false);
    const [blink, setBlink] = useState(true);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [showLaw, setShowLaw] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [summary, setSummary] = useState("");

    // Notas técnicas anteriores: busca e contexto. Nada aqui consome IA — a busca
    // é full-text no banco. O custo só aparece quando uma nota é anexada e passa a
    // compor o input dos turnos seguintes.
    const [notasContexto, setNotasContexto] = useState<INotaContexto[]>([]);
    const [maxNotas, setMaxNotas] = useState(3);
    const [termoBusca, setTermoBusca] = useState('');
    const [buscandoNotas, setBuscandoNotas] = useState(false);
    // Card suspenso no chat: some ao descartar ou ao anexar. Não é persistido.
    const [resultadosBusca, setResultadosBusca] = useState<INotaSimilar[] | null>(null);
    const [erroNotas, setErroNotas] = useState<string | null>(null);

    const noLimiteNotas = notasContexto.length >= maxNotas;

    // 3) Quando o modal abre (open = true), buscar do backend
    useEffect(() => {
        if (!open) return; // só dispara quando open for true
        const fetchData = async () => {
            try {
                const res: DataAcaoPreparatoria =
                    await ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService.Get(
                        idAcaoPreparatoria
                    );

                if (res) {
                    setDataAcaoPreparatoria(res);
                    // EXTRAIR o array interno: res.conversa_ia.conversa_ia
                    if (res.conversa_ia && Array.isArray(res.conversa_ia)) {
                        setConversas(res.conversa_ia)
                        setSummary(res.summary_ia);
                    } else {
                        setConversas([]);
                    }
                }
            } catch (err) {
                console.error('❌ fetchData erro:', err);
            }
        };

        fetchData();
    }, [open]);

    // Notas já anexadas ao contexto desta conversa
    useEffect(() => {
        if (!open) return;
        ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService
            .GetContextoNotas(idAcaoPreparatoria)
            .then(r => { setNotasContexto(r.notas || []); if (r.max_notas) setMaxNotas(r.max_notas); })
            .catch(err => console.error('Erro ao carregar notas do contexto:', err));
    }, [open, idAcaoPreparatoria]);

    const handleBuscarNotas = async () => {
        const termo = termoBusca.trim();
        if (termo.length < 3 || buscandoNotas) return;
        setErroNotas(null);
        setBuscandoNotas(true);
        try {
            const r = await ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService
                .BuscarNotasSimilares(idAcaoPreparatoria, termo);
            setResultadosBusca(r.resultados || []);
            if (r.max_notas) setMaxNotas(r.max_notas);
        } catch (e: any) {
            setErroNotas(e?.message || 'Não foi possível buscar notas similares.');
        } finally {
            setBuscandoNotas(false);
        }
    };

    const handleAnexarNota = async (id_nota: number) => {
        setErroNotas(null);
        try {
            const r = await ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService
                .AnexarNotaContexto(idAcaoPreparatoria, id_nota);
            setNotasContexto(r.notas || []);
            setResultadosBusca(null);   // some o card ao aproveitar
            setTermoBusca('');
        } catch (e: any) {
            setErroNotas(e?.message || 'Não foi possível anexar a nota.');
        }
    };

    const handleDesanexarNota = async (id_nota: number) => {
        setErroNotas(null);
        try {
            const r = await ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService
                .DesanexarNotaContexto(idAcaoPreparatoria, id_nota);
            setNotasContexto(r.notas || []);
        } catch (e: any) {
            setErroNotas(e?.message || 'Não foi possível remover a nota.');
        }
    };

    /** Link para abrir a nota encontrada em nova aba, sem sair da conversa. */
    const linkDaNota = (id_acao_prep: number) =>
        `/proposicao/acao-preparatoria/${id_acao_prep}/nota-tecnica-colaborativa`;

    // 4) Efeito para piscar “Estou pensando...”
    useEffect(() => {
        const interval = setInterval(() => {
            setBlink(prev => !prev);
        }, 500);
        return () => clearInterval(interval);
    }, []);

    // 5) Função para chamar a IA
    const gerarRespostaAI = async (
        id_nota: number,
        data: { lawText: string; userPrompt: string; summary: string }
    ) => {
        try {
            const res = await ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService.openAiInteraction(
                id_nota,
                data
            );
            setBlockSendButton(false);

            // res já é { resposta: string; summary: string }
            return res as { resposta: string; summary: string };
        } catch (err: any) {
            setBlockSendButton(false);
            console.error('Erro ao gerar resposta IA:', err);
            // Propaga o motivo vindo da API para ser exibido como a própria resposta.
            return { resposta: err?.message || 'Não foi possível obter resposta da IA no momento.', summary: '' };
        }
    };

    // 6) Quando o usuário clica em “Enviar”
    const handleSend = async () => {
        if (blockSendButton) return;
        if (!inputValue.trim()) return;

        setBlockSendButton(true);

        const now = new Date().toLocaleString();

        // 1) Mensagem do usuário
        const userMsg: Mensagem = {
            id: `user-${Date.now()}`,
            sender: 'user',
            text: inputValue,
            timestamp: now
        };

        // 2) Mensagem de loading da IA
        const loadingId = `loading-${Date.now()}`;
        const loadingMsg: Mensagem = {
            id: loadingId,
            sender: 'ai',
            text: 'Estou pensando, um min...',
            timestamp: now
        };

        // 3) Otimistic update local: exibe userMsg + loadingMsg
        setConversas(prev => [...prev, userMsg, loadingMsg]);
        setInputValue('');

        // 4) Chama a IA, que AGORA devolve { resposta, summary }
        const iaResult = await gerarRespostaAI(idAcaoPreparatoria, {
            lawText,
            userPrompt: userMsg.text,
            summary: summary
        });
        // iaResult sempre traz `resposta`: em caso de falha, ela carrega o motivo
        // vindo da API (IA desligada, sem crédito, etc.) em vez de um genérico.
        // 5) Substitui o loading e grava tudo:
        const respostaIA = iaResult?.resposta ?? 'Não foi possível obter resposta da IA no momento.';
        const summaryText = iaResult?.summary ?? '';

        const responseTime = new Date().toLocaleString();
        setConversas(prev => {
            // Remove o loading
            const semLoading = prev.filter(m => m.id !== loadingId);

            // Cria a mensagem definitiva da IA
            const iaMsg: Mensagem = {
                id: `ai-${Date.now() + 1}`,
                sender: 'ai',
                text: respostaIA,
                timestamp: responseTime
            };

            // Monta o array final de Mensagem[]
            const finalArray: Mensagem[] = [...semLoading, iaMsg];
            // 6) Atualiza no banco AS DUAS colunas: conversa_ia (JSONB) e summary_ia (text)
            ProposicaoAcaoInfluenciaNotaTecnicaColaborativaService
                .UpdateConversas(idAcaoPreparatoria, finalArray, summaryText)
                .catch(err => console.error('Erro ao salvar conversas no DB:', err));

            return finalArray;
        });

        setBlockSendButton(false);
    };

    // 7) Scroll automático quando `conversas` mudar
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [conversas]);

    // 8) Agrupa em blocos de 2 mensagens (usuário + IA)
    const blocks: Mensagem[][] = [];
    for (let i = 0; i < conversas.length; i += 2) {
        blocks.push(conversas.slice(i, i + 2));
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle
                sx={{ fontFamily: pallet.general.fontFamily, backgroundColor: pallet.backgroundColorPrimary }}
            >
                <Box display="flex" alignItems="center" sx={{ color: pallet.general.color3 }}>
                    <Box component="img" src={maquiHead} alt="Logo Legis IA" sx={{ width: 60, height: 45, mr: 1 }} />
                    Utilize o Legis para construir a nota técnica colaborativa sobre a{' '}
                    {dataAcaoPreparatoria?.proposicao} com IA
                </Box>
            </DialogTitle>

            {/* Notas técnicas anteriores — buscar (grátis) e anexar ao contexto.
                Cores vêm de tokens da paleta, nunca hex fixo: há cliente com
                identidade diferente e hardcode quebraria o tema dele. */}
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    bgcolor: pallet.backgroundColorTertiary,
                    borderBottom: `1px solid ${pallet.backgroungColorTertiaryBorder}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                    <SearchIcon sx={{ fontSize: 18, color: pallet.general.color1 }} />
                    <Typography
                        sx={{
                            fontFamily: pallet.general.fontFamily,
                            fontSize: pallet.general.size12,
                            fontWeight: pallet.general.weight_600,
                            color: pallet.general.color1,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Notas técnicas anteriores
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        value={termoBusca}
                        onChange={e => setTermoBusca(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBuscarNotas(); } }}
                        placeholder="Buscar por tema, título ou expressão…"
                        sx={{
                            minWidth: 280,
                            flex: '0 1 360px',
                            '& .MuiOutlinedInput-root': {
                                bgcolor: pallet.backGroundPaperColorPrimary,
                                '& fieldset': { borderColor: pallet.backgroungColorTertiaryBorder },
                                '&:hover fieldset': { borderColor: pallet.general.color2 },
                                '&.Mui-focused fieldset': { borderColor: pallet.general.color1 },
                            },
                        }}
                        InputProps={{
                            sx: {
                                fontFamily: pallet.general.fontFamily,
                                fontSize: pallet.general.size14,
                                color: pallet.textColorQuaternary,
                            },
                        }}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleBuscarNotas}
                        disabled={termoBusca.trim().length < 3 || buscandoNotas}
                        startIcon={<SearchIcon />}
                        sx={{
                            textTransform: 'none',
                            fontFamily: pallet.general.fontFamily,
                            fontWeight: pallet.general.weight_600,
                            bgcolor: pallet.general.color1,
                            color: pallet.general.color3,
                            boxShadow: 'none',
                            px: 2,
                            '&:hover': { bgcolor: pallet.general.color2, boxShadow: 'none' },
                        }}
                    >
                        {buscandoNotas ? 'Buscando…' : 'Buscar'}
                    </Button>

                    {notasContexto.map(n => (
                        <Chip
                            key={n.id}
                            size="small"
                            label={`${n.proposicao || 'Nota'}${n.criado_em ? ` · ${new Date(n.criado_em).toLocaleDateString()}` : ''}`}
                            onDelete={() => handleDesanexarNota(n.id)}
                            sx={{
                                fontFamily: pallet.general.fontFamily,
                                fontSize: pallet.general.size12,
                                fontWeight: pallet.general.weight_500,
                                bgcolor: pallet.backgroundColorQuintenary,
                                color: pallet.textColorQuintenary,
                                '& .MuiChip-deleteIcon': {
                                    color: pallet.textColorQuintenary,
                                    opacity: 0.7,
                                    '&:hover': { opacity: 1, color: pallet.general.color1 },
                                },
                            }}
                        />
                    ))}
                </Box>

                <Typography
                    sx={{
                        mt: 1,
                        fontSize: '12px',
                        fontStyle: 'italic',
                        color: noLimiteNotas ? '#B26A00' : pallet.general.color4,
                        fontFamily: pallet.general.fontFamily,
                    }}
                >
                    {notasContexto.length}/{maxNotas} notas no contexto — máximo de {maxNotas}. Buscar não consome IA; apenas anexar acrescenta contexto à conversa.
                </Typography>

                {erroNotas && (
                    <Typography sx={{ mt: 0.5, fontSize: '11px', fontStyle: 'italic', color: 'error.main', fontFamily: pallet.general.fontFamily }}>
                        {erroNotas}
                    </Typography>
                )}
            </Box>

            <DialogContent dividers>
                {/* Card suspenso do resultado: some ao descartar ou ao aproveitar. */}
                {resultadosBusca !== null && (
                    <Box
                        mb={2}
                        sx={{
                            border: `1px solid ${pallet.backgroundColorQuintenary}`,
                            borderLeft: `4px solid ${pallet.general.color1}`,
                            borderRadius: '6px',
                            overflow: 'hidden',
                            bgcolor: pallet.backGroundPaperColorPrimary,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 2,
                                py: 1.2,
                                bgcolor: pallet.backgroundColorQuintenary,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: pallet.general.weight_600,
                                    fontSize: pallet.general.size14,
                                    fontFamily: pallet.general.fontFamily,
                                    color: pallet.textColorQuintenary,
                                }}
                            >
                                {resultadosBusca.length > 0
                                    ? `${resultadosBusca.length} nota(s) técnica(s) encontrada(s)`
                                    : 'Nenhuma nota técnica anterior encontrada'}
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => setResultadosBusca(null)}
                                sx={{
                                    textTransform: 'none',
                                    fontFamily: pallet.general.fontFamily,
                                    fontSize: pallet.general.size12,
                                    color: pallet.textColorQuintenary,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                }}
                            >
                                Descartar
                            </Button>
                        </Box>

                        {resultadosBusca.map((r, i) => (
                            <Box
                                key={r.id}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    borderTop: i === 0 ? 'none' : `1px solid ${pallet.backgroungColorTertiaryBorder}`,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: pallet.general.weight_600,
                                        fontSize: pallet.general.size14,
                                        fontFamily: pallet.general.fontFamily,
                                        color: pallet.general.color1,
                                    }}
                                >
                                    {r.proposicao || 'Proposição não identificada'}
                                    {r.criado_em && (
                                        <Typography
                                            component="span"
                                            sx={{
                                                ml: 1,
                                                fontSize: pallet.general.size12,
                                                fontWeight: pallet.general.weight_300,
                                                color: pallet.general.color4,
                                                fontFamily: pallet.general.fontFamily,
                                            }}
                                        >
                                            {new Date(r.criado_em).toLocaleDateString()}
                                        </Typography>
                                    )}
                                </Typography>

                                <Typography
                                    sx={{
                                        whiteSpace: 'pre-line',
                                        fontWeight: pallet.general.weight_300,
                                        fontSize: pallet.general.size12,
                                        fontFamily: pallet.general.fontFamily,
                                        color: pallet.textColorQuaternary,
                                        my: 1,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {r.trecho}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button
                                        size="small"
                                        component="a"
                                        href={linkDaNota(r.id_acao_preparatoria)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            textTransform: 'none',
                                            fontFamily: pallet.general.fontFamily,
                                            fontSize: pallet.general.size12,
                                            color: pallet.general.color2,
                                            '&:hover': { bgcolor: pallet.backgroundColorQuintenary },
                                        }}
                                    >
                                        Abrir na íntegra
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={noLimiteNotas || notasContexto.some(n => n.id === r.id)}
                                        onClick={() => handleAnexarNota(r.id)}
                                        sx={{
                                            textTransform: 'none',
                                            fontFamily: pallet.general.fontFamily,
                                            fontSize: pallet.general.size12,
                                            fontWeight: pallet.general.weight_600,
                                            bgcolor: pallet.general.color1,
                                            color: pallet.general.color3,
                                            boxShadow: 'none',
                                            '&:hover': { bgcolor: pallet.general.color2, boxShadow: 'none' },
                                        }}
                                    >
                                        {notasContexto.some(n => n.id === r.id) ? 'Já no contexto' : 'Usar como contexto'}
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {(lawText || summaryText) && (
                    <>
                        <Box display="flex" justifyContent="center" gap={2} mb={2}>
                            {lawText && (
                                <IconButton onClick={() => setShowLaw(p => !p)} title="Texto da Lei em Análise">
                                    <ArticleIcon />
                                </IconButton>
                            )}
                            {summaryText && (
                                <IconButton
                                    onClick={() => setShowSummary(p => !p)}
                                    title="Texto Sumarizado da Lei em Análise"
                                >
                                    <DescriptionIcon />
                                </IconButton>
                            )}
                        </Box>
                        <Collapse in={showLaw} timeout="auto" unmountOnExit>
                            <Box
                                mb={2}
                                p={2}
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    overflowX: 'hidden'
                                }}
                            >
                                <Grid style={{ fontWeight: 500, marginBottom: '10px' }}>
                                    Texto da Lei em Análise
                                </Grid>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontWeight: 300 }}>
                                    {lawText}
                                </Typography>
                            </Box>
                        </Collapse>
                        <Collapse in={showSummary} timeout="auto" unmountOnExit>
                            <Box
                                mb={2}
                                p={2}
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    overflowX: 'hidden'
                                }}
                            >
                                <Grid style={{ fontWeight: 500, marginBottom: '10px' }}>
                                    Texto Sumarizado da Lei em Análise
                                </Grid>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontWeight: 300 }}>
                                    {summaryText}
                                </Typography>
                            </Box>
                        </Collapse>
                    </>
                )}

                {blocks.length > 0 && (
                    <Box
                        ref={containerRef}
                        mb={2}
                        p={2}
                        sx={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            boxShadow: 'inset 0 0 10px 2px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        {blocks.map((block, idx) => (
                            <React.Fragment key={idx}>
                                <Divider
                                    textAlign="center"
                                    sx={{ mt: 3, mb: 1, '&::before,&::after': { borderTopWidth: '1px' } }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily: pallet.general.fontFamily,
                                            fontSize: pallet.general.size12,
                                            color: 'grey.500'
                                        }}
                                    >
                                        {block[0]?.timestamp}
                                    </Typography>
                                </Divider>
                                {block.map(msg => (
                                    <Box
                                        key={msg.id}
                                        mb={1}
                                        p={2}
                                        sx={{
                                            position: 'relative',
                                            bgcolor: msg.sender === 'ai' ? 'grey.600' : 'white',
                                            borderRadius: 1
                                        }}
                                    >
                                        <Grid container>
                                            <Grid item xs={11}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: pallet.general.fontFamily,
                                                        fontSize: pallet.general.size14,
                                                        color: msg.sender === 'ai' ? 'white' : 'black'
                                                    }}
                                                >
                                                    {msg.sender === 'ai' ? (
                                                        <>
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    fontWeight: 300,
                                                                    fontStyle: 'italic',
                                                                    fontSize: pallet.general.size12,
                                                                    marginRight: 1,
                                                                    color: '#d3d3d3'
                                                                }}
                                                            >
                                                                Legis:
                                                            </Typography>
                                                            {msg.text === 'Estou pensando, um min...' ? (
                                                                <Typography
                                                                    component="span"
                                                                    sx={{
                                                                        fontWeight: 100,
                                                                        fontSize: pallet.general.size14,
                                                                        color: '#39ff14',
                                                                        opacity: blink ? 1 : 0.3,
                                                                        transition: 'opacity 0.3s'
                                                                    }}
                                                                >
                                                                    {msg.text}
                                                                </Typography>
                                                            ) : (
                                                                // O modelo responde em Markdown; sem este renderizador
                                                                // o usuário via "##" e "**" literalmente na tela.
                                                                <Box sx={{ mt: 0.5 }}>
                                                                    <RespostaIA texto={msg.text} />
                                                                </Box>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    fontWeight: 300,
                                                                    fontStyle: 'italic',
                                                                    fontSize: pallet.general.size12,
                                                                    marginRight: 1,
                                                                    color: '#7a7a7a'
                                                                }}
                                                            >
                                                                Você:
                                                            </Typography>
                                                            <Typography component="span" sx={{ fontWeight: 100, fontSize: pallet.general.size14 }}>
                                                                {msg.text === 'Estou pensando, um min...' ? (
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            fontWeight: 100,
                                                                            fontSize: pallet.general.size14,
                                                                            color: '#39ff14',
                                                                            opacity: blink ? 1 : 0.3,
                                                                            transition: 'opacity 0.3s'
                                                                        }}
                                                                    >
                                                                        {msg.text}
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography component="span" sx={{ fontWeight: 100, fontSize: pallet.general.size14 }}>
                                                                        {msg.text}
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Typography>
                                            </Grid>
                                            {msg.sender === 'ai' && (
                                                <Grid item xs={1}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigator.clipboard.writeText(msg.text)}
                                                        sx={{ position: 'absolute', top: 4, right: 4 }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" sx={{ color: 'white' }} />
                                                    </IconButton>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                ))}
                            </React.Fragment>
                        ))}
                    </Box>
                )}

                <Box display="flex" alignItems="flex-end" gap={1}>
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                    />
                    <IconButton disabled={blockSendButton} onClick={handleSend}>
                        <SendIcon
                            style={{
                                color: blockSendButton ? pallet.general.color4 : pallet.general.color1
                            }}
                        />
                    </IconButton>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button style={{ color: pallet.general.color1 }} onClick={onClose}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConversaModal;